import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Medal, Crown, ChevronLeft, ChevronRight, Flame, Star, Clock, CheckCircle, Gift, Award, Calendar } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getInitials } from '../utils/avatarUtils';

const API_URL = '';

/**
 * Full Leaderboard Page
 * Shows complete challenge standings, past winners, and eligibility requirements.
 */
const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challengeData, setChallengeData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/challenges/leaderboard/full?week_offset=${weekOffset}`);
      
      if (response.data.active !== false) {
        setChallengeData(response.data);
      } else {
        setChallengeData(null);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching challenge leaderboard:', err);
      setError('Failed to load leaderboard');
      setLoading(false);
    }
  };

  // Redirect to home if no active challenge
  useEffect(() => {
    if (!loading && !challengeData) {
      navigate('/reviewers');
    }
  }, [loading, challengeData, navigate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-gray-400">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-l-4 border-yellow-500';
      case 2:
        return 'bg-gradient-to-r from-gray-300/10 to-gray-400/10 border-l-4 border-gray-400';
      case 3:
        return 'bg-gradient-to-r from-amber-600/10 to-orange-500/10 border-l-4 border-amber-600';
      default:
        return 'bg-white border-l-4 border-transparent';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-[#F2D857] mx-auto mb-4 animate-pulse" />
          <p className="text-white text-xl">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (!challengeData) {
    return null;
  }

  const { challenge_name, week_start, week_end, leaderboard, previous_week_winners, eligibility_requirements } = challengeData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F2D857]/20 blur-[120px] rounded-full"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Button
            onClick={() => navigate('/reviewers')}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-6"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Reviewers
          </Button>
          
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F2D857] to-amber-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#F2D857]/30">
              <Trophy className="w-10 h-10 text-black" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {challenge_name || 'March Madness'} <span className="text-[#F2D857]">Leaderboard</span>
            </h1>
            <p className="text-xl text-gray-400">
              Compete for weekly prizes by reviewing tracks consistently!
            </p>
          </div>
        </div>
      </section>

      {/* Week Selector */}
      <section className="py-6 border-y border-white/10 bg-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => setWeekOffset(weekOffset - 1)}
              variant="ghost"
              className="text-gray-400 hover:text-white"
              disabled={weekOffset <= -4}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous Week
            </Button>
            
            <div className="text-center">
              <div className="flex items-center gap-2 text-white font-semibold">
                <Calendar className="w-5 h-5 text-[#F2D857]" />
                {weekOffset === 0 ? 'Current Week' : weekOffset === -1 ? 'Last Week' : `${Math.abs(weekOffset)} Weeks Ago`}
              </div>
              <p className="text-sm text-gray-400">
                {formatDate(week_start)} - {formatDate(week_end)}
              </p>
            </div>
            
            <Button
              onClick={() => setWeekOffset(weekOffset + 1)}
              variant="ghost"
              className="text-gray-400 hover:text-white"
              disabled={weekOffset >= 0}
            >
              Next Week
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Prize Cards */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border-yellow-500/30 p-6 text-center">
              <Crown className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">$50</p>
              <p className="text-sm text-gray-400">1st Place</p>
            </Card>
            <Card className="bg-gradient-to-br from-gray-400/20 to-gray-500/20 border-gray-400/30 p-6 text-center">
              <Medal className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">$25</p>
              <p className="text-sm text-gray-400">2nd Place</p>
            </Card>
            <Card className="bg-gradient-to-br from-amber-600/20 to-orange-500/20 border-amber-600/30 p-6 text-center">
              <Medal className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">$10</p>
              <p className="text-sm text-gray-400">3rd Place</p>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30 p-6 text-center">
              <Gift className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">$25</p>
              <p className="text-sm text-gray-400">Weekly Raffle</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Previous Week Winners */}
      {previous_week_winners && previous_week_winners.winners && previous_week_winners.winners.length > 0 && (
        <section className="py-8 bg-gradient-to-r from-[#F2D857]/10 to-amber-500/10 border-y border-[#F2D857]/20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-[#F2D857]" />
              Last Week's Winners
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              {previous_week_winners.winners.map((winner, idx) => (
                <Card key={idx} className="bg-black/50 border-white/10 p-4">
                  <div className="flex items-center gap-3">
                    {getRankIcon(winner.place)}
                    <div>
                      <p className="font-semibold text-white">{winner.name}</p>
                      <p className="text-sm text-gray-400">{winner.points} pts - ${winner.prize_amount}</p>
                    </div>
                  </div>
                </Card>
              ))}
              {previous_week_winners.raffle_winner && (
                <Card className="bg-purple-500/10 border-purple-500/30 p-4">
                  <div className="flex items-center gap-3">
                    <Gift className="w-6 h-6 text-purple-400" />
                    <div>
                      <p className="font-semibold text-white">{previous_week_winners.raffle_winner.name}</p>
                      <p className="text-sm text-purple-400">Raffle - ${previous_week_winners.raffle_winner.prize_amount}</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main Leaderboard */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#F2D857]" />
            {weekOffset === 0 ? 'Current Standings' : 'Week Standings'}
          </h2>

          {leaderboard.length === 0 ? (
            <Card className="bg-white/5 border-white/10 p-12 text-center">
              <Trophy className="w-16 h-16 text-[#F2D857] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-white mb-2">No Activity Yet</h3>
              <p className="text-gray-400 max-w-md mx-auto">
                No reviewers have accumulated challenge points this week yet. 
                Start reviewing to be the first on the leaderboard!
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry) => (
                <Card 
                  key={entry.reviewer_id}
                  className={`p-4 border transition-all hover:shadow-lg ${getRankBg(entry.rank)}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="w-12 h-12 flex items-center justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    
                    {/* Avatar & Name */}
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="w-12 h-12 border-2 border-gray-200">
                        <AvatarImage src={entry.avatar_url} alt={entry.name} />
                        <AvatarFallback className="bg-[#F2D857] text-black font-bold">
                          {getInitials(entry.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-gray-900">{entry.name}</p>
                        <p className="text-sm text-gray-500">@{entry.username}</p>
                      </div>
                    </div>
                    
                    {/* Stats - Desktop */}
                    <div className="hidden md:flex items-center gap-8">
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900">{entry.live_days}</p>
                        <p className="text-xs text-gray-500">Live Days</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900">{Math.round(entry.active_minutes)}</p>
                        <p className="text-xs text-gray-500">Active Min</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900">{entry.reviews}</p>
                        <p className="text-xs text-gray-500">Reviews</p>
                      </div>
                    </div>
                    
                    {/* Points */}
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#F2D857]">{entry.points}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                    
                    {/* Eligibility Badge */}
                    <div className="w-24 text-right">
                      {entry.is_eligible ? (
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Eligible
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 border-gray-300">
                          In Progress
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Stats - Mobile */}
                  <div className="md:hidden flex justify-around mt-4 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">{entry.live_days}</p>
                      <p className="text-xs text-gray-500">Days</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">{Math.round(entry.active_minutes)}</p>
                      <p className="text-xs text-gray-500">Min</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">{entry.reviews}</p>
                      <p className="text-xs text-gray-500">Reviews</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How to Qualify */}
      <section className="py-12 bg-white/5 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-[#F2D857]" />
            How to Qualify
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-black/30 border-white/10 p-6">
              <div className="w-12 h-12 rounded-full bg-[#F2D857]/20 flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-[#F2D857]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {eligibility_requirements?.min_live_days || 3}+ Live Days
              </h3>
              <p className="text-gray-400 text-sm">
                Review tracks on at least {eligibility_requirements?.min_live_days || 3} different days per week.
              </p>
            </Card>
            
            <Card className="bg-black/30 border-white/10 p-6">
              <div className="w-12 h-12 rounded-full bg-[#F2D857]/20 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#F2D857]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {eligibility_requirements?.min_active_minutes_per_day || 30}+ Active Minutes/Day
              </h3>
              <p className="text-gray-400 text-sm">
                Stay actively engaged each live day. Idle time doesn't count - interact with tracks and submissions!
              </p>
            </Card>
            
            <Card className="bg-black/30 border-white/10 p-6">
              <div className="w-12 h-12 rounded-full bg-[#F2D857]/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-[#F2D857]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {eligibility_requirements?.min_reviews_per_day || 3}+ Reviews/Day
              </h3>
              <p className="text-gray-400 text-sm">
                Complete at least {eligibility_requirements?.min_reviews_per_day || 3} reviews each day by moving tracks to Spotlight or Finished.
              </p>
            </Card>
          </div>
          
          <div className="mt-8 p-6 bg-gradient-to-r from-[#F2D857]/10 to-amber-500/10 rounded-xl border border-[#F2D857]/20">
            <h3 className="text-lg font-semibold text-white mb-3">Points System</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <span className="text-[#F2D857]">•</span>
                <span><strong>1 point</strong> per 10 active minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#F2D857]">•</span>
                <span><strong>5 points</strong> per completed review</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#F2D857]">•</span>
                <span><strong>+10 bonus</strong> for 3+ live days</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#F2D857]">•</span>
                <span><strong>+25 bonus</strong> for 5+ live days</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeaderboardPage;
